import React, { useState, useEffect } from "react";
import { apiCall } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCheck, UserX, Shield } from "lucide-react";

const AGENT_PERMISSIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "clients", label: "My Clients" },
  { key: "dialpad", label: "Dialpad" },
  { key: "call_logs", label: "My Call Logs" },
  { key: "conversions", label: "Convert" },
  { key: "b2c_reports", label: "B2C Reports" },
  { key: "broadcast_reports", label: "Broadcast Reports" },
  { key: "chat", label: "Team Chat" },
  { key: "my_salary", label: "My Salary" },
  { key: "extension_download", label: "Chrome Extension Download" },
  { key: "mask_mobile", label: "See Mobile Numbers (unmasked)" },
];

export default function AgentManagement() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const emptyForm = {
    name: "",
    email: "",
    password: "",
    extension: "",
    outbound_caller_id: "",
    sip_server: "voice2.bonvoice.com",
    sip_password: "",
    permissions: ["dashboard", "clients", "dialpad", "call_logs", "conversions", "chat", "my_salary", "broadcast_reports", "b2c_reports"]
  };
  const [form, setForm] = useState(emptyForm);

  const fetchAgents = async () => {
    try {
      const { data } = await apiCall("get", "/agents");
      setAgents(data);
    } catch { toast.error("Failed to load agents"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAgents(); }, []);

  const togglePerm = (key) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions?.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...(prev.permissions || []), key]
    }));
  };

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Name, email and password required"); return; }
    try {
      await apiCall("post", "/agents", form);
      toast.success("Agent created");
      setShowCreate(false);
      setForm(emptyForm);
      fetchAgents();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleUpdate = async () => {
    if (!editAgent) return;
    try {
      await apiCall("put", `/agents/${editAgent._id}`, {
        name: form.name,
        extension: form.extension,
        outbound_caller_id: form.outbound_caller_id,
        sip_server: form.sip_server,
        permissions: form.permissions
      });
      toast.success("Agent updated");
      setEditAgent(null);
      fetchAgents();
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this agent?")) return;
    try { await apiCall("delete", `/agents/${id}`); toast.success("Deleted"); fetchAgents(); } catch { toast.error("Failed"); }
  };

  const toggleActive = async (agent) => {
    try { await apiCall("put", `/agents/${agent._id}`, { is_active: !agent.is_active }); toast.success("Updated"); fetchAgents(); } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="agent-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Agent Management</h2>
          <p className="text-sm text-muted-foreground mt-1">{agents.length}/7 agents created</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setShowCreate(true); }} disabled={agents.length >= 7} data-testid="create-agent-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Agent
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {agents.map(agent => (
          <Card key={agent._id} className="border border-border hover:-translate-y-0.5 transition-all duration-200" data-testid={`agent-card-${agent._id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">{agent.name?.[0]?.toUpperCase()}</div>
                  <div>
                    <p className="font-semibold text-sm">{agent.name}</p>
                    <p className="text-xs text-muted-foreground">{agent.email}</p>
                  </div>
                </div>
                <Badge variant={agent.is_online ? "default" : "secondary"} className="text-xs">{agent.is_online ? "Online" : "Offline"}</Badge>
              </div>
              <div className="space-y-1 text-xs text-muted-foreground mb-4">
                <p>Extension: <span className="text-foreground font-medium">{agent.extension || "N/A"}</span></p>
                <p>Outbound CallerID: <span className="text-foreground font-medium">{agent.outbound_caller_id || "N/A"}</span></p>
                <p>SIP Server: <span className="text-foreground font-medium">{agent.sip_server || "N/A"}</span></p>
                <p>Callback: <span className={agent.callback_enabled !== false ? "text-green-500" : "text-red-500"}>{agent.callback_enabled !== false ? "Enabled" : "Disabled"}</span></p>
                <p>Status: <span className={agent.is_active ? "text-green-500" : "text-red-500"}>{agent.is_active ? "Active" : "Inactive"}</span>{agent.is_blocked && <Badge className="ml-1 bg-red-500 text-white text-[9px]">BLOCKED</Badge>}</p>
              </div>
              <div className="flex gap-1 flex-wrap">
                <Button
                  variant="ghost" size="sm" className="h-7 text-xs"
                  onClick={() => {
                    setForm({
                      name: agent.name,
                      email: agent.email,
                      password: "",
                      extension: agent.extension || "",
                      outbound_caller_id: agent.outbound_caller_id || "",
                      sip_server: agent.sip_server || "",
                      sip_password: "",
                      permissions: agent.permissions || ["dashboard", "clients", "dialpad", "call_logs", "conversions", "chat", "my_salary", "broadcast_reports", "b2c_reports"]
                    });
                    setEditAgent(agent);
                  }}
                  data-testid={`edit-agent-${agent._id}`}
                >
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => toggleActive(agent)}>
                  {agent.is_active ? <UserX className="w-3 h-3 mr-1" /> : <UserCheck className="w-3 h-3 mr-1" />}
                  {agent.is_active ? "Off" : "On"}
                </Button>
                {agent.is_blocked && <Button variant="ghost" size="sm" className="h-7 text-xs text-orange-500" onClick={async () => { try { await apiCall("post", "/admin/unblock-user", { user_id: agent._id }); toast.success("Unblocked"); fetchAgents(); } catch { toast.error("Failed"); } }}>Unblock</Button>}
                <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => handleDelete(agent._id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-agent-dialog">
          <DialogHeader><DialogTitle>Create Agent</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9" data-testid="agent-name-input" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="h-9" data-testid="agent-email-input" /></div>
              <div><Label className="text-xs">Password</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="h-9" data-testid="agent-password-input" /></div>
              <div><Label className="text-xs">Extension</Label><Input value={form.extension} onChange={e => setForm({ ...form, extension: e.target.value })} placeholder="13952" className="h-9" data-testid="agent-extension-input" /></div>
              <div><Label className="text-xs">Caller ID</Label><Input value={form.outbound_caller_id} onChange={e => setForm({ ...form, outbound_caller_id: e.target.value })} placeholder="8065295444" className="h-9" data-testid="agent-callerid-input" /></div>
              <div><Label className="text-xs">SIP Server</Label><Input value={form.sip_server} onChange={e => setForm({ ...form, sip_server: e.target.value })} className="h-9" data-testid="agent-sip-server-input" /></div>
              <div><Label className="text-xs">SIP Password</Label><Input type="password" value={form.sip_password} onChange={e => setForm({ ...form, sip_password: e.target.value })} className="h-9" data-testid="agent-sip-password-input" /></div>
            </div>

            {/* Permissions */}
            <div className="border-t border-border pt-3">
              <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider block mb-2">
                Permissions (Select sections this agent can access)
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {AGENT_PERMISSIONS.map(p => (
                  <label key={p.key} className="flex items-center gap-2 text-xs p-1.5 rounded-md hover:bg-muted/50 cursor-pointer">
                    <Checkbox
                      checked={form.permissions?.includes(p.key)}
                      onCheckedChange={() => togglePerm(p.key)}
                    />
                    <span>{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreate} data-testid="save-agent-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editAgent} onOpenChange={() => setEditAgent(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-agent-dialog">
          <DialogHeader><DialogTitle>Edit Agent & Permissions</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9" /></div>
              <div><Label className="text-xs">Extension</Label><Input value={form.extension} onChange={e => setForm({ ...form, extension: e.target.value })} className="h-9" /></div>
              <div><Label className="text-xs">Caller ID</Label><Input value={form.outbound_caller_id} onChange={e => setForm({ ...form, outbound_caller_id: e.target.value })} className="h-9" /></div>
              <div className="col-span-2"><Label className="text-xs">SIP Server</Label><Input value={form.sip_server} onChange={e => setForm({ ...form, sip_server: e.target.value })} className="h-9" /></div>
            </div>

            {/* Permissions */}
            <div className="border-t border-border pt-3">
              <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider block mb-2">
                Permissions (Select sections this agent can access)
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {AGENT_PERMISSIONS.map(p => (
                  <label key={p.key} className="flex items-center gap-2 text-xs p-1.5 rounded-md hover:bg-muted/50 cursor-pointer">
                    <Checkbox
                      checked={form.permissions?.includes(p.key)}
                      onCheckedChange={() => togglePerm(p.key)}
                    />
                    <span>{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="outline" size="sm" onClick={() => setEditAgent(null)}>Cancel</Button>
            <Button size="sm" onClick={handleUpdate} data-testid="update-agent-btn">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
